/**
 * DEPT Quick-Add "+" System  (v18 — revert to plain innerHTML swap,
 * abandon renderContents() entirely)
 *
 * After three attempts using cart-drawer.js's renderContents() (v15, v16,
 * v17) all produced cart-layout damage in live testing despite passing
 * static code review, the renderContents() integration itself is the
 * common failure point — something about how it re-parses and re-injects
 * nested custom elements (<cart-drawer-items>) is not behaving as the
 * static code implies it should in the actual browser environment.
 *
 * This version stops trying to use that method at all. It goes back to
 * the simpler, more direct approach: fetch the cart-drawer section HTML,
 * extract ONLY the .drawer__inner fragment with DOMParser, and replace
 * the live .drawer__inner's content directly with plain innerHTML —
 * no custom renderContents() call, no nested re-parsing assumptions.
 * Then open the drawer using the SAME class-toggle mechanism
 * cart-drawer.js's own open() method uses (add 'animate'+'active'),
 * since that part of cart-drawer.js was never in question.
 *
 * Also restores the SINGLE-REQUEST add-to-cart (sections included in
 * the /cart/add.js call) since that part is sound and reduces latency
 * regardless of how the response is then applied to the DOM.
 */
(function () {
  'use strict';

  const cache = new Map();
  const CACHE_MAX = 40;
  function cacheSet(key, value) {
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(key, value);
  }

  /* Add to cart AND fetch fresh section HTML in ONE request. */
  async function addToCartWithSections(variantId, qty) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: qty || 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  /* Extract just the inner fragment of a given selector from a full
     section HTML string — simple, direct, no custom-element assumptions. */
  function extractFragment(sectionHtml, selector) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  /* Directly swap the live drawer's content and open it — no
     renderContents(), no reliance on cart-drawer.js's internal merge
     logic at all beyond the open()/close() class toggle, which is the
     one part of that file proven to work correctly throughout testing. */
  function applyCartResponse(response) {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) {
      window.location.href = '/cart';
      return;
    }

    const sections = response.sections || {};

    // Cart-drawer section: replace the inner content of #CartDrawer
    // directly, plain and simple.
    if (sections['cart-drawer']) {
      const fragment = extractFragment(sections['cart-drawer'], '#CartDrawer');
      const liveCartDrawer = document.getElementById('CartDrawer');
      if (fragment != null && liveCartDrawer) {
        liveCartDrawer.innerHTML = fragment;
      }
    }

    // Cart count badge: update directly from the add response, which
    // already contains item_count — no section-id matching needed.
    const count = (response.item_count != null) ? response.item_count
      : (response.items ? response.items.reduce((n, it) => n + it.quantity, 0) : null);
    if (count != null) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const iconWrap = bubble.querySelector('.bad_cart_icon');
        if (iconWrap) {
          let badge = iconWrap.querySelector('.bag-count-badge');
          if (count > 0) {
            const display = count > 99 ? '99+' : String(count);
            if (badge) badge.textContent = display;
            else {
              badge = document.createElement('span');
              badge.className = 'bag-count-badge';
              badge.textContent = display;
              iconWrap.appendChild(badge);
            }
          } else if (badge) {
            badge.remove();
          }
        } else {
          bubble.querySelectorAll('span').forEach((span) => {
            if (/^\s*\[/.test(span.textContent)) span.textContent = '[' + count + ']';
          });
        }
      }
    }

    // Remove is-empty state if present, drop in the fresh content's
    // empty/non-empty class as needed.
    cartDrawerEl.classList.toggle('is-empty', count === 0);

    // Open using the exact same mechanism cart-drawer.js's open() uses.
    setTimeout(() => {
      cartDrawerEl.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    });
  }

  /* ── POPUP: dedicated backdrop + card, like coddiwomple.com ── */
  let backdrop = null;
  let card = null;

  function getElements() {
    if (backdrop && card) return { backdrop, card };

    backdrop = document.createElement('div');
    backdrop.id = 'dept-global-popup-backdrop';

    card = document.createElement('div');
    card.id = 'dept-global-popup';

    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    const style = document.createElement('style');
    style.textContent =
      '#dept-global-popup-backdrop{position:fixed;inset:0;z-index:-1;visibility:hidden;display:none;' +
        'align-items:flex-end;justify-content:center;background:rgba(0,0,0,.5);}' +
      '#dept-global-popup-backdrop.active{display:flex!important;visibility:visible!important;z-index:10000!important;}' +
      '@media (min-width:750px){#dept-global-popup-backdrop.active{align-items:center;}}' +
      '#dept-global-popup{max-height:100%;overflow:auto;}' +
      '#dept-global-popup .popup__close-btn{cursor:pointer;}' +
      '#dept-global-popup .popup__close-btn svg,#dept-global-popup .popup__close-btn svg *{pointer-events:none;}';
    document.head.appendChild(style);

    backdrop.addEventListener('click', () => closePopup());
    card.addEventListener('click', (e) => e.stopPropagation());

    return { backdrop, card };
  }

  /* ── OPEN POPUP ── instant for cached, no spinner flash */
  async function openPopup(handle) {
    const { backdrop: bd, card: c } = getElements();

    const cached = cache.get(handle);
    if (cached && Date.now() - cached.ts < 60000) {
      c.innerHTML = cached.html;
      initPopup(c);
      bd.classList.add('active');
      return;
    }

    try {
      const html = await fetch('/products/' + handle + '?section_id=product-popup-upsell').then(r => r.text());
      cacheSet(handle, { html, ts: Date.now() });
      c.innerHTML = html;
      initPopup(c);
      bd.classList.add('active');
    } catch (err) {
      console.error('[DEPT popup]', err);
    }
  }

  function closePopup() {
    if (!backdrop) return;
    backdrop.classList.remove('active');
  }

  /* ── PARSE EMBEDDED VARIANT DATA ── */
  function getVariantData(c) {
    const el = c.querySelector('.popup-variant-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { console.error('[DEPT] bad variant json', e); return null; }
  }

  function readSelected(c) {
    const selected = {};
    c.querySelectorAll('.product_popup_variant').forEach((group) => {
      const pos = Number(group.dataset.optionPosition);
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) selected[pos] = (checked.dataset.value || checked.value || '').toLowerCase();
    });
    return selected;
  }

  function recomputeAvailability(c, data) {
    const selected = readSelected(c);
    c.querySelectorAll('.product_popup_variant').forEach((group) => {
      const pos = Number(group.dataset.optionPosition);
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const thisVal = (input.dataset.value || input.value || '').toLowerCase();
        const combos = data.variants.filter((v) => {
          if (!v.available) return false;
          if ((String(v.options[pos - 1] || '')).toLowerCase() !== thisVal) return false;
          for (const p in selected) {
            if (Number(p) === pos) continue;
            if ((String(v.options[Number(p) - 1] || '')).toLowerCase() !== selected[p]) return false;
          }
          return true;
        });
        const available = combos.length > 0;
        input.disabled = !available;
        input.classList.toggle('disabled', !available);
      });
    });
  }

  function resolveVariant(c, data) {
    const selected = readSelected(c);
    const match = data.variants.find((v) =>
      Object.keys(selected).every((p) =>
        (String(v.options[Number(p) - 1] || '')).toLowerCase() === selected[p]
      ) && Object.keys(selected).length === data.options.length
    );
    return match || null;
  }

  function formatMoney(cents) { return 'LE ' + (cents / 100).toFixed(2); }

  function syncState(c, data) {
    recomputeAvailability(c, data);
    const variant = resolveVariant(c, data);
    const hiddenInput = c.querySelector('input[name="id"]');
    const atc = c.querySelector('[name="add"]');
    if (variant) {
      if (hiddenInput) hiddenInput.value = variant.id;
      const priceEl = c.querySelector('.product__price__popup');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (atc) atc.disabled = !variant.available;
    } else {
      if (atc) atc.disabled = true;
    }
  }

  function updateSelectedLabel(c, radio) {
    const group = radio.closest('.product_popup_variant');
    if (!group) return;
    const labelSpan = group.previousElementSibling;
    if (labelSpan && labelSpan.classList.contains('popup_variant_label')) {
      const valueSpan = labelSpan.querySelector('[data-selected-value]');
      if (valueSpan) valueSpan.textContent = radio.dataset.displayValue || radio.value;
    }
  }

  /* ── INIT ONE POPUP RENDER ── */
  function initPopup(c) {
    const data = getVariantData(c);

    c.onclick = (e) => {
      if (e.target.closest('.popup__close-btn')) { closePopup(); return; }

      const label = e.target.closest('.swatch-input__label, .popup_variant_detail label');
      if (label) {
        e.preventDefault();
        let input = label.previousElementSibling;
        if (!input || input.tagName !== 'INPUT') {
          const wrapper = label.closest('.popup_variant_detail') || label.parentElement;
          input = wrapper ? wrapper.querySelector('input[type="radio"]') : null;
        }
        if (input && !input.disabled) {
          const group = input.closest('.product_popup_variant');
          if (group) group.querySelectorAll('input[type="radio"]').forEach((r) => { r.checked = (r === input); });
          else input.checked = true;
          updateSelectedLabel(c, input);
          if (data) syncState(c, data);
        }
        return;
      }

      const atc = e.target.closest('[name="add"]');
      if (atc) {
        e.preventDefault();
        const form = atc.closest('form');
        if (form) handleSubmit(form, c, data, atc);
      }
    };

    if (data) syncState(c, data);

    if (!document._deptEscBound) {
      document._deptEscBound = true;
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePopup(); });
    }
  }

  /* ── FORM SUBMIT (popup) ── */
  async function handleSubmit(form, c, data, atcBtn) {
    const originalText = atcBtn.textContent;
    atcBtn.disabled = true;
    atcBtn.textContent = 'ADDING…';
    try {
      let variantId = null;
      if (data) {
        const v = resolveVariant(c, data);
        if (v) variantId = v.id;
      }
      if (!variantId) variantId = form.querySelector('input[name="id"]')?.value;
      if (!variantId) throw new Error('Please select options');

      const response = await addToCartWithSections(variantId, 1);
      closePopup();
      applyCartResponse(response);
      atcBtn.textContent = originalText;
      atcBtn.disabled = false;
    } catch (err) {
      console.error('[DEPT ATC]', err);
      atcBtn.textContent = (err && err.message) ? err.message : 'Try again';
      setTimeout(() => { atcBtn.textContent = originalText; atcBtn.disabled = false; }, 1800);
    }
  }

  /* ── DIRECT ADD (single-variant card buttons) ── */
  async function directAdd(variantId, btn) {
    const prev = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const response = await addToCartWithSections(variantId, 1);
      applyCartResponse(response);
      btn.innerHTML = prev;
      btn.disabled = false;
    } catch (e) {
      console.error('[DEPT ATC]', e);
      btn.textContent = '✕';
      setTimeout(() => { btn.innerHTML = prev; btn.disabled = false; }, 1500);
    }
  }

  /* ── DELEGATED "+" CLICK ── */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.dept-quick-add-btn, .featured-dept-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    const handle = btn.dataset.productHandle;
    if (variantId) directAdd(variantId, btn);
    else if (handle) openPopup(handle);
  });
})();
